@if (settings(); as s) {
  <div class="mx-auto flex max-w-[1400px] flex-col gap-6">

    <!-- HERO CARD — sticky under the 50px titlebar (reachabilities pattern).
         mb-5 + container gap-6 match the user-account page's hero/sub-hero spacing. -->
    <div class="mb-5 sticky top-[50px] z-20 rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex min-w-0 flex-col gap-1">
          <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
            Invoice · Customer {{ sessionService.customerId() }}
          </p>
          <h1 class="font-display text-2xl font-semibold leading-tight text-foreground">
            {{ sessionService.customer()?.companyName || 'Invoice' }}
          </h1>
          <p class="text-sm text-muted-foreground">
            Invoicing day <strong class="font-mono-feat text-foreground">{{ s.invoiceDay }}</strong>
          </p>
        </div>
        <div class="flex flex-none items-center gap-2 pt-1">
          <button type="button" hlmBtn variant="outline" (click)="toggleAll()">
            <hlm-icon name="lucideChevronsUpDown" size="0.9rem" />
            {{ allCollapsed() ? 'Expand all' : 'Collapse all' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Sub-hero action row: Manual Invoice generation dialog launcher (UA pattern) -->
    <div class="-mt-4 mb-1 flex flex-wrap items-start justify-end gap-1">
      <button hlmBtn variant="default" (click)="openManualInvoiceDialog()" [disabled]="editDisabled()">
        <hlm-icon name="lucideReceipt" size="0.9rem" />
        Manual Invoice
      </button>
    </div>

    <!-- INVOICE SETTINGS (collapsible, 2-column) -->
    <section [cmtRevealOnExpand]="!isCollapsed('settings')" class="rounded-2xl border border-border bg-card shadow-sm">
      <header
        class="sticky top-[164px] z-10 flex cursor-pointer items-center gap-2.5 rounded-2xl bg-card p-5"
        [class.shadow-sm]="!isCollapsed('settings')"
        (click)="toggleSection('settings')"
      >
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <hlm-icon name="lucideSettings" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Invoice Settings</h2>
        <hlm-icon
          [name]="isCollapsed('settings') ? 'lucideChevronDown' : 'lucideChevronUp'"
          size="1rem"
          class="ml-auto text-muted-foreground"
        />
      </header>

      @if (!isCollapsed('settings')) {
        <div class="p-5 pt-0" (click)="$event.stopPropagation()">
          <!-- 2-column key/value grid (freed by removing the Manual Invoice column) -->
          <dl class="grid grid-cols-1 gap-x-8 md:grid-cols-2">
            <div class="flex items-center justify-between gap-4 border-b border-border/70 py-2.5">
              <dt class="cmt-eyebrow">Billing Language</dt>
              <dd class="min-w-0 flex-1 text-right">
                @if (editMode()) {
                  <select
                    hlmNativeSelect
                    class="ml-auto max-w-[200px]"
                    [formControl]="settingsForm.controls.billingLanguage"
                  >
                    @for (lang of languages(); track lang.id) {
                      <option [ngValue]="lang.id">{{ lang.name }}</option>
                    }
                  </select>
                } @else {
                  <span class="text-sm font-medium text-foreground">{{ billingLanguageName() }}</span>
                }
              </dd>
            </div>

            <div class="flex items-center justify-between gap-4 border-b border-border/70 py-2.5">
              <dt class="cmt-eyebrow">Usage Fee Limit</dt>
              <dd class="min-w-0 flex-1 text-right">
                @if (editMode()) {
                  <input
                    hlmInput size="sm"
                    class="ml-auto max-w-[100px] text-right"
                    type="number"
                    [formControl]="settingsForm.controls.usageFeeLimit"
                  />
                } @else {
                  <span class="font-mono-feat text-sm text-foreground">{{ s.usageFeeLimit }}</span>
                }
              </dd>
            </div>

            <div class="flex items-center justify-between gap-4 border-b border-border/70 py-2.5">
              <dt class="cmt-eyebrow">Invoice Day</dt>
              <dd class="min-w-0 flex-1 text-right">
                @if (editMode()) {
                  <input
                    hlmInput size="sm"
                    class="ml-auto max-w-[100px] text-right"
                    type="number"
                    [formControl]="settingsForm.controls.invoiceDay"
                  />
                } @else {
                  <span class="font-mono-feat text-sm text-foreground">{{ s.invoiceDay }}</span>
                }
              </dd>
            </div>

            <div class="flex items-center justify-between gap-4 border-b border-border/70 py-2.5">
              <dt class="cmt-eyebrow">Billing Enabled</dt>
              <dd class="min-w-0 flex-1 text-right">
                @if (editMode() && canEditBilling) {
                  <select
                    hlmNativeSelect
                    class="ml-auto max-w-[160px]"
                    [formControl]="settingsForm.controls.billingEnabled"
                  >
                    <option [ngValue]="true">Yes</option>
                    <option [ngValue]="false">No</option>
                  </select>
                } @else {
                  <span class="text-sm font-medium text-foreground">{{ formatBool(s.billingEnabled ?? true) }}</span>
                }
                @if (editMode() && !canEditBilling) {
                  <div class="mt-2 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-left">
                    <hlm-icon name="lucideTriangleAlert" size="0.9rem" class="mt-0.5 flex-none text-warning" />
                    <p class="text-sm text-foreground">
                      Changing a customer's billing status is a high-impact action. It requires the
                      <em>billing-status</em> permission — ask a team lead.
                    </p>
                  </div>
                }
              </dd>
            </div>

            <div class="flex items-center justify-between gap-4 border-b border-border/70 py-2.5">
              <dt class="cmt-eyebrow">Payment Term</dt>
              <dd class="min-w-0 flex-1 text-right">
                @if (editMode()) {
                  <input
                    hlmInput size="sm"
                    class="ml-auto max-w-[100px] text-right"
                    type="number"
                    [formControl]="settingsForm.controls.paymentTerm"
                  />
                } @else {
                  <span class="font-mono-feat text-sm text-foreground">{{ s.paymentTerm }}</span>
                }
              </dd>
            </div>

            <div class="flex items-center justify-between gap-4 border-b border-border/70 py-2.5">
              <dt class="cmt-eyebrow">PDF Background</dt>
              <dd [class]="'text-right text-sm font-medium ' + (s.pdfBackground ? 'text-foreground' : 'text-muted-foreground/60')">
                {{ s.pdfBackground || '—' }}
              </dd>
            </div>
          </dl>

          <!-- Feature chips for boolean settings -->
          <div class="mt-4 flex flex-wrap gap-2">
            <hlm-badge [variant]="s.firstBillImmediately ? 'accent' : 'outline'">
              First Bill Immediately
              <span class="font-mono-feat opacity-70">{{ s.firstBillImmediately | boolDisplay }}</span>
            </hlm-badge>
            <hlm-badge [variant]="s.showInvoiceByAccounts ? 'accent' : 'outline'">
              Invoice by Accounts
              <span class="font-mono-feat opacity-70">{{ s.showInvoiceByAccounts | boolDisplay }}</span>
            </hlm-badge>
            <hlm-badge [variant]="s.showUnitPrice ? 'accent' : 'outline'">
              Show Unit Price
              <span class="font-mono-feat opacity-70">{{ s.showUnitPrice | boolDisplay }}</span>
            </hlm-badge>
          </div>

          <!-- Actions -->
          <div class="mt-4 flex flex-wrap items-center gap-2">
            @if (!editMode()) {
              <button hlmBtn variant="default" (click)="startEdit()" [disabled]="editDisabled()">
                <hlm-icon name="lucidePencil" size="0.9rem" />
                Edit
              </button>
            } @else {
              <button hlmBtn variant="default" (click)="save()" [disabled]="settingsForm.invalid || saving()">
                @if (saving()) {
                  <hlm-spinner size="16" />
                  Saving…
                } @else {
                  <hlm-icon name="lucideSave" size="0.9rem" />
                  Save
                }
              </button>
              <button hlmBtn variant="outline" (click)="cancelEdit()">Cancel</button>
            }
          </div>
        </div>
      }
    </section>

    <!-- INVOICES OVERVIEW (collapsible) -->
    <section #invoicesSection [cmtRevealOnExpand]="!isCollapsed('invoices')" class="rounded-2xl border border-border bg-card shadow-sm">
      <header
        class="sticky top-[164px] z-10 flex cursor-pointer items-center gap-2.5 rounded-2xl bg-card p-5"
        [class.shadow-sm]="!isCollapsed('invoices')"
        (click)="toggleSection('invoices')"
      >
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <hlm-icon name="lucideFileText" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Invoices</h2>
        @if (!loadingInvoices()) {
          <hlm-badge variant="secondary" class="ml-1 text-sm">{{ filteredInvoices().length }}</hlm-badge>
        }
        <hlm-icon
          [name]="isCollapsed('invoices') ? 'lucideChevronDown' : 'lucideChevronUp'"
          size="1rem"
          class="ml-auto text-muted-foreground"
        />
      </header>

      @if (!isCollapsed('invoices')) {
        <div class="p-5 pt-4">
          <div class="sticky top-[228px] z-10 -mx-5 -mt-4 mb-4 flex items-center gap-2 border-b border-border/60 bg-card/95 px-5 py-3 backdrop-blur-sm">
            <div class="flex h-8 w-72 items-center gap-1.5 rounded-md border border-input bg-background pl-2.5 pr-1 text-sm focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/25">
              <hlm-icon name="lucideSearch" size="0.9rem" class="text-muted-foreground" />
              <input
                type="text"
                class="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70"
                placeholder="Search by invoice number or date…"
                [value]="searchQuery()"
                (input)="onSearchInput($event)"
              />
              @if (searchQuery()) {
                <button
                  type="button"
                  hlmTooltip="Clear search"
                  (click)="clearInvoiceSearch()"
                  class="grid h-6 w-6 flex-none place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                  aria-label="Clear search"
                >
                  <hlm-icon name="lucideX" size="0.8rem" />
                </button>
              }
            </div>
            <button hlmBtn variant="ghost" size="icon" hlmTooltip="Refresh" (click)="refreshInvoices()">
              <hlm-icon name="lucideRefreshCw" size="0.9rem" />
            </button>
          </div>

          @if (loadingInvoices()) {
            <div class="flex items-center gap-3 p-6 text-sm text-muted-foreground">
              <hlm-spinner size="20" />
              Loading invoices…
            </div>
          } @else if (filteredInvoices().length === 0) {
            <div class="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center text-sm text-muted-foreground">
              @if (searchQuery()) {
                No invoices match your search
              } @else {
                No invoices found
              }
            </div>
          } @else {
            <div class="overflow-hidden rounded-xl border border-border">
              <!-- Cap the body to ~5 rows (header + 5 × ~37px ≈ 22rem) then scroll internally (EB-3883) -->
              <div class="max-h-[22rem] overflow-auto">
                <table class="w-full text-sm">
                  <thead class="sticky top-0 z-10 bg-muted/80 backdrop-blur">
                    <tr class="font-mono-feat text-sm uppercase tracking-[0.16em] text-muted-foreground">
                      <th class="cursor-pointer px-3 py-2 text-left" (click)="onSortColumn('showReId')">Invoice #</th>
                      <th class="cursor-pointer px-3 py-2 text-left" (click)="onSortColumn('creationDate')">Date</th>
                      <th class="px-3 py-2 text-left">Base Fee</th>
                      <th class="px-3 py-2 text-left">Service Fee</th>
                      <th class="cursor-pointer px-3 py-2 text-right" (click)="onSortColumn('basePrice')">Base</th>
                      <th class="cursor-pointer px-3 py-2 text-right" (click)="onSortColumn('unitPrice')">Service</th>
                      <th class="cursor-pointer px-3 py-2 text-right" (click)="onSortColumn('total')">Total</th>
                      <th class="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (inv of filteredInvoices(); track inv.id) {
                      <tr class="border-t border-border text-foreground transition-colors hover:bg-muted/40">
                        <td class="px-3 py-2 font-mono-feat">
                          <!-- old CMT pinguin-invoice list: bold "No invoice number assigned" (EB-4396) -->
                          @if (inv.showReId) { {{ inv.showReId }} } @else { <strong class="font-sans font-semibold">No invoice number assigned</strong> }
                        </td>
                        <td class="px-3 py-2 font-mono-feat">{{ inv.creationDate | date:'dd.MM.yyyy' }}</td>
                        <td class="px-3 py-2 text-muted-foreground">
                          {{ inv.baseFeeStartDate | date:'dd.MM.yyyy' }} — {{ inv.baseFeeEndDate | date:'dd.MM.yyyy' }}
                        </td>
                        <td class="px-3 py-2 text-muted-foreground">
                          {{ inv.serviceFeeStartDate | date:'dd.MM.yyyy' }} — {{ inv.serviceFeeEndDate | date:'dd.MM.yyyy' }}
                        </td>
                        <td class="px-3 py-2 text-right font-mono-feat">{{ inv.basePrice | number:'1.2-2' }}</td>
                        <td class="px-3 py-2 text-right font-mono-feat">{{ inv.unitPrice | number:'1.2-2' }}</td>
                        <td class="px-3 py-2 text-right font-mono-feat font-semibold">
                          {{ (inv.basePrice ?? 0) + (inv.unitPrice ?? 0) | number:'1.2-2' }}
                        </td>
                        <td class="px-3 py-2 text-right">
                          <div class="flex items-center justify-end gap-1">
                            <!-- Both are the EVN (old CMT "EVN" / "EVN excel"); the icon tells preview from download -->
                            <button hlmBtn variant="outline" size="sm" title="EVN preview (PDF)" (click)="downloadEvnPdf(inv)">
                              <hlm-icon name="lucideEye" size="0.9rem" />
                              EVN
                            </button>
                            <button hlmBtn variant="outline" size="sm" title="Download EVN as Excel" (click)="downloadEvnExcel(inv)">
                              <hlm-icon name="lucideDownload" size="0.9rem" />
                              EVN Excel
                            </button>
                            <!-- Detail = the row's primary action (Style Guide 7.1: one primary, rest outline) -->
                            <button hlmBtn variant="default" size="sm" (click)="downloadDetail(inv)">Detail</button>
                          </div>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              <div class="border-t border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                <span class="font-mono-feat">{{ filteredInvoices().length }} invoice{{ filteredInvoices().length === 1 ? '' : 's' }}</span>
              </div>
            </div>
          }
        </div>
      }
    </section>

    <!-- USAGE CREDIT (Nutzungsguthaben, EB-3883) — between Invoices and Billing; default collapsed -->
    <cmt-usage-credit
      [cmtRevealOnExpand]="!creditCollapsed()"
      [invoices]="invoices()"
      [(collapsed)]="creditCollapsed"
      (jumpToInvoice)="jumpToInvoice($event)"
      (invoiceResolved)="onInvoiceResolved($event)"
    />

    <!-- BILLING DETAILS (CRUD on raw BILLING events) — default collapsed (EB-3883) -->
    <cmt-customer-billing
      #billingSection
      [cmtRevealOnExpand]="!billingCollapsed()"
      [invoices]="invoices()"
      [(collapsed)]="billingCollapsed"
      (jumpToInvoice)="jumpToInvoice($event)"
      (invoiceResolved)="onInvoiceResolved($event)"
    />

  </div>
}
